import React, { useState, useEffect, useCallback } from 'react';
import { useDolphin } from '../../hooks/useDolphin';
import { Bar } from 'react-chartjs-2';
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
// import { useWallet } from '../../context/WalletContext';
import { FaChartBar, FaUsers, FaChartLine, FaClock, FaTrophy } from 'react-icons/fa';
import toast from 'react-hot-toast';

import '../../styles/global.css';
Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);


// Helper to decode Dolphin.ActionType
const actionTypeToString = (action) => {
  if (action === 0) return 'Mine';
  if (action === 1) return 'Claim';
  if (action === 2) return 'Register';
  if (action === 3) return 'BuyLevel';
  if (action === 4) return 'ReferralPayout';
  if (action === 5) return 'FixedPayout';
  if (action === 6) return 'MiningPayout';
  if (action === 7) return 'USDTMine';
  return 'Unknown';
};

const AdminUserActivity = () => {
  const { dolphinContract, getUserActivities } = useDolphin();
  // Global analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState('');

  // Prepare chart data for quick stats (must be inside component)
  const chartData = {
    labels: ['Total Users', 'Mining Volume', 'USDT Mining'],
    datasets: [
      {
        label: 'Network Stats',
        data: [
          Number(analytics?.totalUsers) || 0,
          Number(analytics?.totalMiningVolume) || 0,
          Number(analytics?.totalUSDTMiningVolume) || 0
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)'
        ],
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    scales: {
      y: { beginAtZero: true }
    }
  };
  // User activity lookup state
  const [activityAddress, setActivityAddress] = useState('');
  const [userActivities, setUserActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');

  // Fetch global analytics on mount
  useEffect(() => {
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError('');
      try {
        if (!dolphinContract) return;
        // getAnalytics() should return all fields in order
        const result = await dolphinContract.getAnalytics();
        setAnalytics({
          totalUsers: result[0]?.toString(),
          totalLevelPurchases: result[1]?.toString(),
          totalMiningVolume: result[2]?.toString(),
          totalUSDTMiningVolume: result[3]?.toString(),
          totalReferralPayouts: result[4]?.toString(),
          totalFixedPayouts: result[5]?.toString(),
          totalMiningPayouts: result[6]?.toString(),
          totalRevenue: result[7]?.toString(),
          lastUpdated: result[8]?.toString(),
        });
      } catch (err) {
        setAnalyticsError('Failed to fetch analytics');
      }
      setAnalyticsLoading(false);
    };
    fetchAnalytics();
  }, [dolphinContract]);

  // User activity lookup logic
  const handleFetchActivities = useCallback(async () => {
    setActivityLoading(true);
    setActivityError('');
    setUserActivities([]);
    try {
      const activities = await getUserActivities(activityAddress);
      setUserActivities(activities);
    } catch (err) {
      setActivityError(err.message || 'Failed to fetch activities');
    }
    setActivityLoading(false);
  }, [activityAddress, getUserActivities]);

  // Listen for ActivityLogged events
  useEffect(() => {
    if (!dolphinContract || !activityAddress || activityAddress.length !== 42) return;
    const onActivityLogged = (user) => {
      if (user.toLowerCase() === activityAddress.toLowerCase()) {
        handleFetchActivities();
        toast.success('User activity updated');
      }
    };
    dolphinContract.on('ActivityLogged', onActivityLogged);
    return () => {
      dolphinContract.off('ActivityLogged', onActivityLogged);
    };
  }, [dolphinContract, activityAddress, handleFetchActivities]);


  return (
    <div className="admin-analytics-sleek-root">
      <div className="admin-analytics-header">
        <FaChartBar className="admin-analytics-header-icon" />
        <h2>Admin Analytics & User Activity</h2>
      </div>

      {/* Sleek Quick Stats Grid */}
      <div className="admin-analytics-stats-grid">
        {analyticsLoading ? (
          <div className="admin-analytics-loading">Loading analytics...</div>
        ) : analyticsError ? (
          <div className="admin-analytics-error">{analyticsError}</div>
        ) : (
          <>
            {/* ...existing stat cards... */}
            <div className="admin-analytics-stat-card users">
              <FaUsers className="stat-icon" />
              <div className="stat-label">Total Users</div>
              <div className="stat-value">{Number(analytics?.totalUsers) || 0}</div>
            </div>
            {/* ...other stat cards... */}
          </>
        )}
      </div>

      {/* Sleek Analytics Bar Chart */}
      <div className="admin-analytics-chart-section">
        <div className="admin-analytics-chart-header">
          <FaChartLine className="chart-header-icon" />
          <span>Network Overview</span>
        </div>
        <Bar data={chartData} options={chartOptions} height={80} />
      </div>

      {/* Sleek Earnings Breakdown Table */}
      <div className="admin-analytics-breakdown-section">
        <div className="admin-analytics-breakdown-header">
          <FaTrophy className="breakdown-header-icon" />
          <span>Earnings Breakdown</span>
        </div>
        <table className="admin-analytics-breakdown-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Referral Payouts</td><td>{Number(analytics?.totalReferralPayouts) || 0}</td></tr>
            <tr><td>Fixed Payouts</td><td>{Number(analytics?.totalFixedPayouts) || 0}</td></tr>
            <tr><td>Mining Payouts</td><td>{Number(analytics?.totalMiningPayouts) || 0}</td></tr>
            <tr><td>Total Revenue</td><td>{Number(analytics?.totalRevenue) || 0}</td></tr>
          </tbody>
        </table>
      </div>

      {/* User Activity Lookup Section */}
      <div className="admin-user-activity-lookup">
        <h3>User Activity Lookup</h3>
        <input
          type="text"
          value={activityAddress}
          onChange={e => setActivityAddress(e.target.value)}
          placeholder="Enter user address (0x...)"
          style={{width:'340px',marginBottom:'1rem',padding:'8px',fontSize:'1rem',borderRadius:'8px',border:'1.5px solid #38bdf8'}}
        />
        <button
          onClick={handleFetchActivities}
          disabled={!activityAddress || activityLoading}
          style={{marginLeft:'12px',padding:'8px 18px',fontSize:'1rem',borderRadius:'8px',background:'#38bdf8',color:'#fff',fontWeight:600,border:'none',cursor:activityLoading?'not-allowed':'pointer'}}
        >{activityLoading ? 'Loading...' : 'Fetch Activities'}</button>
        {activityError && <div style={{color:'#ef4444',marginTop:'8px'}}>{activityError}</div>}
        {userActivities && userActivities.length > 0 && (
          <table className="admin-user-activity-table" style={{marginTop:'1.5rem',width:'100%',borderCollapse:'collapse',background:'#181a2a',borderRadius:'12px',overflow:'hidden'}}>
            <thead>
              <tr style={{background:'#23263a',color:'#a78bfa'}}>
                <th style={{padding:'10px'}}>Action</th>
                <th style={{padding:'10px'}}>Amount</th>
                <th style={{padding:'10px'}}>Timestamp</th>
                <th style={{padding:'10px'}}>Related Address</th>
                <th style={{padding:'10px'}}>Data</th>
              </tr>
            </thead>
            <tbody>
              {userActivities.map((act, idx) => (
                <tr key={idx} style={{borderBottom:'1px solid #23263a',color:'#fff'}}>
                  <td style={{padding:'10px'}}>{actionTypeToString(act.action)}</td>
                  <td style={{padding:'10px'}}>{act.amount?.toString?.() || act.amount}</td>
                  <td style={{padding:'10px'}}>{act.timestamp ? new Date(Number(act.timestamp) * 1000).toLocaleString() : ''}</td>
                  <td style={{padding:'10px',fontFamily:'monospace',fontSize:'0.95em'}}>{act.relatedAddress || '--'}</td>
                  <td style={{padding:'10px',fontFamily:'monospace',fontSize:'0.95em'}}>{act.data || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent Activity Section (placeholder for future) */}
      <div className="admin-analytics-recent-activity">
        <div className="recent-activity-header">
          <FaClock className="recent-activity-icon" />
          <span>Recent Network Activity</span>
        </div>
        <div className="recent-activity-placeholder">Coming soon: Activity feed, top earners, and more charts!</div>
      </div>

    </div>
  );


};

export default AdminUserActivity;